import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listSubmissionsForDeal,
  getSubmissionQuotations,
  listCarrierRegistry,
} from '@/services/submission.service.js';

// Data spine for the Submission Builder matrix (Slice 4b). One refreshable
// fetch chain: deal → active submission (first non-abandoned) → grouped
// scenarios feed (label/coverage/hull/is_selected + quotes rows). The
// carrier registry loads once alongside (names, routing_type for the
// Materialize-vs-Add-quote affordance).

export default function useSubmissionBuilder(dealId) {
  const [submission, setSubmission] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(!!dealId);
  const [error, setError] = useState(null);
  const aborted = useRef(false);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listSubmissionsForDeal(dealId);
      const active = (r?.submissions || []).find((s) => s.status !== 'abandoned') || null;
      let groups = [];
      if (active) {
        const q = await getSubmissionQuotations(active.id);
        groups = q?.scenarios || [];
      }
      if (!aborted.current) {
        setSubmission(active);
        setScenarios(groups);
      }
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    aborted.current = false;
    if (dealId) refresh();
    return () => {
      aborted.current = true;
    };
  }, [dealId, refresh]);

  useEffect(() => {
    let cancelled = false;
    listCarrierRegistry()
      .then((rows) => {
        if (!cancelled) setCarriers((Array.isArray(rows) ? rows : []).filter((c) => c.active !== false));
      })
      .catch(() => {}); // registry failure degrades to slug-only columns; api.js already toasts
    return () => {
      cancelled = true;
    };
  }, []);

  return { submission, scenarios, carriers, loading, error, refresh };
}
